import {
	createContext,
	useContext,
	useEffect,
	useState,
	ReactNode,
	useRef,
} from 'react'
import {
	supabase,
	transferSessionFromMainDomain,
	saveSessionToSharedStorage,
} from '../integrations/supabase/client'
import { clearAuthCache } from '../lib/api'
import { Session, User } from '@supabase/supabase-js'
import { sentryUtils } from '../lib/sentry'
import { safeStorage } from '@/utils/storage'

interface AuthContextType {
	session: Session | null
	user: User | null
	loading: boolean // Represents ongoing session refreshes, not initial load
	isInitialized: boolean // True after the first session check is complete
	error: Error | null
	signIn: (email: string, password: string) => Promise<void>
	signOut: () => Promise<void>
	syncSession: () => Promise<boolean>
}

// Create a default context value to prevent undefined errors during hot reload
const defaultContextValue: AuthContextType = {
	session: null,
	user: null,
	loading: true,
	isInitialized: false,
	error: null,
	signIn: async () => {
		console.warn('AuthProvider not initialized - signIn called')
	},
	signOut: async () => {
		console.warn('AuthProvider not initialized - signOut called')
		// Optionally redirect to auth page even without context
		if (typeof window !== 'undefined') {
			window.location.href = '/auth'
		}
	},
	syncSession: async () => false,
}

const AuthContext = createContext<AuthContextType>(defaultContextValue)

export function AuthProvider({ children }: { children: ReactNode }) {
	const [session, setSession] = useState<Session | null>(null)
	const [user, setUser] = useState<User | null>(null)
	const [loading, setLoading] = useState(true)
	const [isInitialized, setIsInitialized] = useState(false)
	const [error, setError] = useState<Error | null>(null)
	const isMountedRef = useRef(true)

	const syncSession = async () => {
		if (!isMountedRef.current) {
			return false
		}

		const success = await transferSessionFromMainDomain()

		if (success && isMountedRef.current) {
			// Force a session refresh after transfer
			const {
				data: { session: currentSession },
			} = await supabase.auth.getSession()
			if (currentSession && isMountedRef.current) {
				setSession(currentSession)
				setUser(currentSession.user)
			}
		}

		return success
	}

	useEffect(() => {
		isMountedRef.current = true

		// Ensure initialization always completes within 5 seconds max
		const initializationTimeout = setTimeout(() => {
			if (isMountedRef.current && !loading) return // Already completed

			console.warn('[AuthContext] Initialization timeout - forcing completion')
			if (isMountedRef.current) {
				setLoading(false)
				setIsInitialized(true)
			}
		}, 5000)

		// Set up periodic session refresh to proactively refresh tokens
		const sessionRefreshInterval = setInterval(async () => {
			if (!isMountedRef.current) return
			
			try {
				// Check current session and refresh if needed
				const { data: { session } } = await supabase.auth.getSession()
				
				if (session) {
					// Parse token to check expiry
					try {
						const tokenPayload = JSON.parse(
							atob(session.access_token.split('.')[1])
						)
						const expiryTime = tokenPayload.exp * 1000
						const now = Date.now()
						const timeUntilExpiry = expiryTime - now
						
						// If token expires in less than 5 minutes, refresh it
						if (timeUntilExpiry < 5 * 60 * 1000) {
							const { error } = await supabase.auth.refreshSession()
							if (!error) {
								// Save refreshed session
								await saveSessionToSharedStorage()
							}
						}
					} catch (e) {
						// Token parsing failed, ignore
					}
				}
			} catch (error) {
				// Ignore refresh errors in interval
			}
		}, 30000) // Check every 30 seconds

		// Get initial session
		const initializeAuth = async () => {
			const startTime = Date.now()
			try {
				setLoading(true)

				const {
					data: { session },
					error: sessionError,
				} = await supabase.auth.getSession()

				if (sessionError) {
					console.error('[AuthContext] Session error:', sessionError)
					// Don't throw - continue with null session
				}

				// if (session) {
				// 	console.log('[AuthContext] Session found:', {
				// 		userId: session.user?.id,
				// 		email: session.user?.email,
				// 		expiresAt: session.expires_at
				// 	})
				// } else {
				// 	console.log('[AuthContext] No session found')
				// }

				if (isMountedRef.current) {
					setSession(session)
					setUser(session?.user ?? null)
				}
			} catch (err: any) {
				console.error(
					`[AuthContext] Error during initialization after ${
						Date.now() - startTime
					}ms:`,
					err
				)
				if (isMountedRef.current) setError(err)
			} finally {
				clearTimeout(initializationTimeout)
				if (isMountedRef.current) {
					setLoading(false)
					setIsInitialized(true)
				}
			}
		}

		// Set up auth state listener first (before initialization)
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(async (event, session) => {
			if (!isMountedRef.current) return

			setSession(session)
			setUser(session?.user ?? null)

			// Handle different auth events
			if (event === 'TOKEN_REFRESHED') {
				// Clear API cache when token is refreshed to use new token
				clearAuthCache()
			}
			
			// Update Sentry user context
			if (session?.user) {
				sentryUtils.setUser({
					id: session.user.id,
					email: session.user.email,
					username: session.user.user_metadata?.full_name,
				})
				sentryUtils.addBreadcrumb(
					`User signed in: ${session.user.email}`,
					'auth'
				)
			} else {
				sentryUtils.clearUser()
				sentryUtils.addBreadcrumb('User signed out', 'auth')
			}

			// Save session in background - don't await to prevent blocking
			if (session) {
				saveSessionToSharedStorage().catch((err) =>
					console.warn(
						'[AuthContext] Failed to save session to shared storage:',
						err
					)
				)
			}
		})

		// Start initialization
		initializeAuth()

		return () => {
			clearTimeout(initializationTimeout)
			clearInterval(sessionRefreshInterval)
			isMountedRef.current = false
			subscription?.unsubscribe()
		}
	}, [])

	const signIn = async (email: string, password: string) => {
		try {
			const { error } = await supabase.auth.signInWithPassword({
				email,
				password,
			})
			if (error) throw error

			// Save session after successful sign in
			await saveSessionToSharedStorage()
		} catch (error) {
			setError(error as Error)
			throw error
		}
	}

	const signOut = async () => {
		try {
			// Call Supabase signOut first to clear session on server
			const { error } = await supabase.auth.signOut()
			if (error) {
				console.error('[AuthContext] Supabase signOut error:', error)
			}
		} catch (error) {
			console.error('[AuthContext] Supabase signOut error:', error)
		} finally {
			// Always clear local state regardless of API call success
			setSession(null)
			setUser(null)
			setError(null)
			clearAuthCache()

			// Clear all auth-related storage
			localStorage.removeItem('shared-sb-auth-token')

			// Clear Supabase storage keys
			const keysToRemove = Object.keys(localStorage).filter(key =>
				key.startsWith('sb-') || key.includes('supabase')
			)
			keysToRemove.forEach(key => localStorage.removeItem(key))

			// Clear onboarding completion flags for all users
			// (We don't have the user ID at this point, so clear all onboarding flags)
			const onboardingKeys = Object.keys(localStorage).filter(key =>
				key.startsWith('onboarding_complete_')
			)
			onboardingKeys.forEach(key => safeStorage.removeItem(key))

			// Also clear selected program ID to prevent stale data
			safeStorage.removeItem('selectedProgramId')
		}
	}

	const value = {
		session,
		user,
		loading,
		isInitialized,
		error,
		signIn,
		signOut,
		syncSession,
	}

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
	const context = useContext(AuthContext)

	// During hot reload, context might temporarily be the default value
	// Return it instead of throwing to prevent app crashes
	if (!context || context === defaultContextValue) {
		console.warn('useAuth called outside of AuthProvider or during hot reload')
		return defaultContextValue
	}

	return context
}
